/** @jsxImportSource @emotion/react */

import { FC, useEffect } from 'react'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import { format, utcToZonedTime } from 'date-fns-tz'
import { useParams } from 'react-router-dom'
import { PlatformSlug } from 'constants/consts'
import { findOneUser_findOneUser as User } from '__generatedTypes__/findOneUser'
import { UserBlockHistoryStatusEnum } from '__generatedTypes__/globalTypes'
import Table, { FiltersWrapper, PaginationPage } from 'components/Table'
import Loader from 'components/Loader'
import { getPlatformFullNameBySlug } from 'utils/getPlatformName'
import * as tabsClasses from '../styles'
import * as classes from './styles'
import { useWarningHistory } from './useWarningHistory'

const timeZone = 'America/New_York'

type Props = {
  user: User
}

const MAP_WARNING_STATUS = {
  [UserBlockHistoryStatusEnum.AccountBlocked]: 'Account Blocked',
  [UserBlockHistoryStatusEnum.AccountUnblocked]: 'Account Unblocked',
  [UserBlockHistoryStatusEnum.ScreenshotWarning]: 'Screenshot warning',
  [UserBlockHistoryStatusEnum.TestWarning]: 'Multiple practice test warning',
  [UserBlockHistoryStatusEnum.CreationWarning]: 'Multiple custom test creation warning',
}

export const Warning: FC<Props> = ({ user }) => {
  const { id: userId, screenshotInfo } = user
  const { platform: platformSlug } = useParams()

  const warningHistoryState = useWarningHistory(userId)

  const { items, count, loading, pagination, refetch } = warningHistoryState

  useEffect(() => {
    refetch()
  }, [refetch])

  const cmsPlatformName = getPlatformFullNameBySlug(platformSlug as PlatformSlug)
  const totalScreenshotWarnings =
    screenshotInfo?.find((info) => info.platform === cmsPlatformName)?.totalScreenshotsCount ?? 0

  const renderWarningTable = () => {
    if (loading) {
      return <Loader />
    }

    return (
      <div css={classes.tableWrapper}>
        <Table
          css={classes.table}
          columns={[
            {
              title: 'Warning #',
              renderCell: (warning) => <span>{warning.counter ? `#${warning.counter}` : '-'}</span>,
              flex: 1,
            },
            {
              title: 'Description',
              renderCell: (warning) => <span>{MAP_WARNING_STATUS[warning.status]}</span>,
              flex: 2,
            },
            {
              title: 'Date',
              renderCell: (warning) => (
                <span>{format(utcToZonedTime(new Date(warning.createdAt), timeZone), 'MM/dd/yyyy', { timeZone })}</span>
              ),
              flex: 1,
            },
            {
              title: 'Time Incident Occured',
              renderCell: (warning) => (
                <span>
                  {format(utcToZonedTime(new Date(warning.createdAt), timeZone), 'h:mm a', { timeZone })} (EST)
                </span>
              ),
              flex: 2,
            },
          ]}
          items={items}
          wrapperMinWidth={620}
          wrapperPadding={8}
        />
        <div css={classes.tableFooter}>
          <FiltersWrapper>
            <span>{count ?? 0} items</span>
          </FiltersWrapper>
          <PaginationPage {...pagination} />
        </div>
      </div>
    )
  }

  return (
    <section css={tabsClasses.container}>
      <div css={tabsClasses.form}>
        <div css={tabsClasses.heading}>
          <Typography variant="title3" css={tabsClasses.title}>
            Warning Data for User
          </Typography>
          <Typography variant="body2" css={tabsClasses.totalScreenshotWarningsText}>
            Total Screenshots made by user for the selected platform: {totalScreenshotWarnings}
          </Typography>
        </div>
        <Divider />

        {renderWarningTable()}
      </div>
    </section>
  )
}
